import React, { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Pencil, Check, X } from 'lucide-react';

// Alaska Airlines elite status tiers (2024 rebrand)
const STATUS_TIERS = [
  { name: 'Silver', points: 20000 },
  { name: 'Gold', points: 40000 },
  { name: 'Platinum', points: 75000 },
  { name: 'Titanium', points: 100000 },
];

// Tier color mapping (approximate metal tones)
const TIER_COLORS = {
  Silver: { bg: '#C0C0C0', text: '#111827' },
  Gold: { bg: '#D4AF37', text: '#111827' },
  Platinum: { bg: '#E5E4E2', text: '#111827' },
  Titanium: { bg: '#8A8A8A', text: '#F9FAFB' },
};

export default function StatusSummaryNew({
  flights,
  loading,
  currentStatusPoints = 0,
  onUpdateCurrentPoints,
  isLoggedIn = false,
  atmosSummitBonus = false,
  onAtmosSummitBonusChange
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const totalPoints = flights.reduce((sum, flight) => sum + flight.status_points, 0);
  const bonusPoints = atmosSummitBonus ? 10000 : 0;
  const projectedPoints = currentStatusPoints + totalPoints + bonusPoints;

  const handleEditStart = () => {
    setEditValue(currentStatusPoints.toString());
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleEditSave = () => {
    if (onUpdateCurrentPoints) {
      onUpdateCurrentPoints(editValue);
    }
    setIsEditing(false);
    setEditValue('');
  };

  if (loading) {
    return (
      <div className="p-4">
        <Card className="bg-card border-border shadow-sm p-4 animate-pulse">
          <div className="mb-4">
            <div className="h-3 bg-muted rounded w-32 mb-2"></div>
            <div className="h-10 bg-muted rounded w-40"></div>
          </div>
          <div className="h-6 bg-muted rounded w-24 mb-3"></div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded w-full"></div>
            <div className="h-2 bg-muted rounded w-full"></div>
          </div>
        </Card>
      </div>
    );
  }

  const displayPoints = isLoggedIn ? projectedPoints : totalPoints;

  const getCurrentTier = () => {
    for (let i = STATUS_TIERS.length - 1; i >= 0; i--) {
      if (displayPoints >= STATUS_TIERS[i].points) {
        return STATUS_TIERS[i];
      }
    }
    return null;
  };

  const getNextTier = () => {
    for (let tier of STATUS_TIERS) {
      if (displayPoints < tier.points) {
        return tier;
      }
    }
    return null;
  };

  const currentTier = getCurrentTier();
  const nextTier = getNextTier();
  const progress = nextTier ? (displayPoints / nextTier.points) * 100 : 100;

  return (
    <div className="p-4">
      <Card className="bg-card border-border shadow-sm p-4">
        {/* Points Display - Different for logged in vs not logged in */}
        {isLoggedIn ? (
          <>
            {/* Current Status Points (Editable) */}
            <div className="mb-3">
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                Current Status Points
              </div>
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="text-2xl font-bold text-accent bg-background border border-border rounded px-2 py-1 w-32"
                      autoFocus
                    />
                    <button
                      onClick={handleEditSave}
                      className="p-1 hover:bg-accent/20 rounded"
                      title="Save"
                    >
                      <Check className="h-4 w-4 text-green-600" />
                    </button>
                    <button
                      onClick={handleEditCancel}
                      className="p-1 hover:bg-destructive/20 rounded"
                      title="Cancel"
                    >
                      <X className="h-4 w-4 text-destructive" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="text-2xl font-bold text-accent">
                      {currentStatusPoints.toLocaleString()}
                    </div>
                    <button
                      onClick={handleEditStart}
                      className="p-1 hover:bg-accent/20 rounded"
                      title="Edit current points"
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Atmos Summit 10k Bonus Checkbox */}
            <div className="mb-3">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={atmosSummitBonus}
                  onChange={(e) => onAtmosSummitBonusChange && onAtmosSummitBonusChange(e.target.checked)}
                  className="w-4 h-4 rounded border-border text-accent focus:ring-accent focus:ring-offset-0"
                />
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  Add Atmos Summit 10k Bonus
                </span>
              </label>
            </div>

            {/* Projected Status Points (Read-only) */}
            <div className="mb-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                Projected Status Points
              </div>
              <div className="text-4xl font-bold text-primary">
                {projectedPoints.toLocaleString()}
              </div>
            </div>
          </>
        ) : (
          /* Not logged in - show total */
          <div className="mb-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
              Total Status Points
            </div>
            <div className="text-4xl font-bold text-accent">
              {totalPoints.toLocaleString()}
            </div>
          </div>
        )}

        {/* Current Tier */}
        {currentTier && (
          <div className="mb-3">
            <Badge
              variant="default"
              className="text-sm px-3 py-1"
              style={{
                backgroundColor: TIER_COLORS[currentTier.name]?.bg,
                color: TIER_COLORS[currentTier.name]?.text,
              }}
            >
              {currentTier.name}
            </Badge>
          </div>
        )}

        {/* Progress to Next Tier */}
        {nextTier ? (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Next: {nextTier.name}</span>
              <span className="font-medium">
                {(nextTier.points - displayPoints).toLocaleString()} pts
              </span>
            </div>
            <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-300"
                style={{
                  width: `${Math.min(progress, 100)}%`,
                  backgroundColor: TIER_COLORS[nextTier.name]?.bg,
                }}
              />
            </div>
          </div>
        ) : (
          currentTier && (
            <div className="text-sm text-accent font-medium">
              🎉 Maximum tier achieved!
            </div>
          )
        )}

        {!currentTier && nextTier && (
          <div className="text-sm text-muted-foreground">
            {(nextTier.points - displayPoints).toLocaleString()} points to {nextTier.name}
          </div>
        )}
      </Card>
    </div>
  );
}
